import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ProductCard from '../components/ProductCard';
import { ShoppingBag, Minus, Plus, ArrowLeft, Star, Heart, Truck, Shield, Check, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ rating = 4.5, count = 19 }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star key={i} className={`h-4 w-4 ${i <= Math.floor(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`} />
        ))}
      </div>
      <span className="text-sm text-[var(--dark)]/40">({count})</span>
    </div>
  );
}

function ImageCarousel({ images = [] }) {
  const [current, setCurrent] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const imgs = images.length > 0 ? images : ['/placeholder.jpg'];

  const next = () => setCurrent(c => (c + 1) % imgs.length);
  const prev = () => setCurrent(c => (c - 1 + imgs.length) % imgs.length);

  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? next() : prev(); }
    setTouchStart(null);
  };

  return (
    <div className="space-y-3" data-testid="image-carousel">
      <div
        className="relative aspect-square rounded-2xl overflow-hidden bg-[var(--cream)] group"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={imgs[current]}
          alt=""
          className="w-full h-full object-cover transition-opacity duration-300"
          data-testid="main-product-image"
        />
        {imgs.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-white"
              data-testid="carousel-prev"
            >
              <ChevronLeft className="h-4 w-4 text-[var(--dark)]" />
            </button>
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md hover:bg-white"
              data-testid="carousel-next"
            >
              <ChevronRight className="h-4 w-4 text-[var(--dark)]" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {imgs.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-[var(--violet)] w-5' : 'bg-white/70'}`}
                  data-testid={`carousel-dot-${i}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
      {imgs.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="image-thumbnails">
          {imgs.map((img, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${i === current ? 'border-[var(--violet)] ring-1 ring-[var(--violet)]/30' : 'border-transparent opacity-50 hover:opacity-80'}`}
              data-testid={`thumbnail-${i}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AccordionSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[var(--sage)]/15">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full py-4 text-left" data-testid={`accordion-${title}`}>
        <span className="text-sm font-semibold text-[var(--dark)]">{title}</span>
        {open ? <ChevronUp className="h-4 w-4 text-[var(--dark)]/40" /> : <ChevronDown className="h-4 w-4 text-[var(--dark)]/40" />}
      </button>
      {open && <div className="pb-4 text-sm text-[var(--dark)]/60 leading-relaxed">{children}</div>}
    </div>
  );
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [liked, setLiked] = useState(false);
  const [related, setRelated] = useState([]);
  const { addItem } = useCart();
  const { user, token } = useAuth();

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(r => { setProduct(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (product?.category) {
      axios.get(`${API}/products?category=${product.category}`).then(r => {
        setRelated(r.data.filter(p => p.id !== product.id).slice(0, 4));
      }).catch(() => {});
    }
  }, [product]);

  // Load wishlist state
  useEffect(() => {
    if (token && product) {
      axios.get(`${API}/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => { if (r.data.product_ids?.includes(product.id)) setLiked(true); })
        .catch(() => {});
    }
  }, [token, product]);

  const toggleWishlist = async () => {
    if (!user) return;
    try {
      if (liked) {
        await axios.delete(`${API}/wishlist/${product.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setLiked(false);
      } else {
        await axios.post(`${API}/wishlist/${product.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setLiked(true);
      }
    } catch { /* empty */ }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white"><div className="animate-spin w-8 h-8 border-2 border-[var(--violet)] border-t-transparent rounded-full"></div></div>;
  if (!product) return <div className="min-h-screen flex items-center justify-center bg-white text-[var(--dark)]/50">Producto no encontrado</div>;

  const hasDiscount = product.discount_price && product.discount_price < product.price;
  const price = hasDiscount ? product.discount_price : product.price;
  const outOfStock = product.stock !== undefined && product.stock <= 0;

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      <div className="bg-[var(--cream)] border-b border-[var(--sage)]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-xs text-[var(--dark)]/40">
            <Link to="/" className="hover:text-[var(--violet)]">Inicio</Link>
            <span>/</span>
            <Link to="/tienda" className="hover:text-[var(--violet)]">Tienda</Link>
            <span>/</span>
            <span className="text-[var(--dark)]/60 truncate max-w-[200px]">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Image Carousel */}
          <div>
            <ImageCarousel images={product.images} />
            {hasDiscount && (
              <Badge className="mt-3 bg-[var(--violet)] text-white border-0 text-xs font-bold px-3 py-1 rounded-lg">
                -{Math.round((1 - product.discount_price / product.price) * 100)}% OFF
              </Badge>
            )}
          </div>

          {/* Details */}
          <div className="space-y-5">
            <div>
              <p className="text-sm text-[var(--dark)]/40 font-medium mb-1" data-testid="product-brand">{product.brand}</p>
              <StarRating rating={4.5} count={Math.floor(Math.random() * 30) + 5} />
            </div>

            <h1 className="text-xl sm:text-2xl font-bold text-[var(--dark)] leading-tight" data-testid="product-title">{product.name}</h1>

            {/* Short description */}
            {product.short_description && (
              <p className="text-sm text-[var(--dark)]/60 leading-relaxed" data-testid="short-description">{product.short_description}</p>
            )}

            {/* Price */}
            <div className="flex items-baseline gap-3">
              {hasDiscount && <span className="text-lg text-[var(--dark)]/25 line-through">${product.price?.toFixed(2)}</span>}
              <span className={`text-2xl sm:text-3xl font-bold ${hasDiscount ? 'text-[var(--violet)]' : 'text-[var(--dark)]'}`} data-testid="product-price">${price?.toFixed(2)}</span>
            </div>

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="product-tags">
                {product.tags.map(t => (
                  <Badge key={t} className="bg-[var(--violet-light)] text-[var(--violet)] border-0 text-[10px] font-medium px-2 py-0.5 rounded-md">{t}</Badge>
                ))}
              </div>
            )}

            {/* Skin types */}
            {product.skin_types?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {product.skin_types.map(t => (
                  <Badge key={t} variant="outline" className="border-[var(--sage)]/40 text-[var(--dark)]/50 bg-[var(--cream)] capitalize text-[10px] font-medium px-2.5 py-0.5 rounded-md">{t}</Badge>
                ))}
              </div>
            )}

            {/* Stock info */}
            {product.stock !== undefined && (
              <div className="flex items-center gap-2" data-testid="stock-info">
                {outOfStock ? (
                  <Badge className="bg-red-50 text-red-600 border-0 text-xs font-semibold px-3 py-1">Agotado</Badge>
                ) : product.stock <= 5 ? (
                  <Badge className="bg-orange-50 text-orange-600 border-0 text-xs font-semibold px-3 py-1">Solo quedan {product.stock} unidades</Badge>
                ) : (
                  <Badge className="bg-green-50 text-green-600 border-0 text-xs font-semibold px-3 py-1">{product.stock} disponibles</Badge>
                )}
              </div>
            )}

            {/* Quantity + Add to cart */}
            <div className="flex items-center gap-3 pt-2">
              <div className="flex items-center border border-[var(--sage)]/40 rounded-lg overflow-hidden">
                <button className="h-11 w-11 flex items-center justify-center hover:bg-[var(--cream)] transition-colors disabled:opacity-30" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" disabled={outOfStock}>
                  <Minus className="h-4 w-4 text-[var(--dark)]/50" />
                </button>
                <span className="w-12 text-center font-semibold text-sm border-x border-[var(--sage)]/40" data-testid="qty-display">{qty}</span>
                <button className="h-11 w-11 flex items-center justify-center hover:bg-[var(--cream)] transition-colors disabled:opacity-30" onClick={() => setQty(Math.min(qty + 1, product.stock || 999))} data-testid="qty-plus" disabled={outOfStock}>
                  <Plus className="h-4 w-4 text-[var(--dark)]/50" />
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                size="lg"
                className={`flex-1 rounded-xl h-12 text-sm font-semibold tracking-wide ${outOfStock ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[var(--dark)] hover:bg-[var(--dark)]/90 text-white'}`}
                onClick={() => !outOfStock && addItem(product, qty)}
                disabled={outOfStock}
                data-testid="add-to-cart-detail-btn"
              >
                <ShoppingBag className="mr-2 h-4 w-4" /> {outOfStock ? 'Agotado' : 'Agregar al carrito'}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className={`rounded-xl h-12 w-12 border-[var(--sage)]/30 ${liked ? 'text-red-500' : 'text-[var(--dark)]/30'}`}
                onClick={toggleWishlist}
                data-testid="wishlist-btn"
              >
                <Heart className={`h-5 w-5 ${liked ? 'fill-red-500' : ''}`} />
              </Button>
            </div>

            {/* Trust badges */}
            <div className="flex gap-4 pt-2 text-[10px] text-[var(--dark)]/40">
              <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> Envio Gratis PR</span>
              <span className="flex items-center gap-1"><Shield className="h-3.5 w-3.5" /> 100% Autentico</span>
              <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5" /> K-Beauty Original</span>
            </div>

            {/* Accordion sections */}
            <div className="pt-4">
              <AccordionSection title="Descripcion" defaultOpen={true}>
                <p className="whitespace-pre-wrap">{product.description}</p>
              </AccordionSection>
              {product.benefits?.length > 0 && (
                <AccordionSection title="Beneficios">
                  <ul className="space-y-2">
                    {product.benefits.map((b, i) => (
                      <li key={i} className="flex items-start gap-2"><Check className="h-4 w-4 text-[var(--sage-dark)] mt-0.5 flex-shrink-0" />{b}</li>
                    ))}
                  </ul>
                </AccordionSection>
              )}
              {product.ingredients?.length > 0 && (
                <AccordionSection title="Ingredientes">
                  <div className="flex flex-wrap gap-1.5">
                    {product.ingredients.map((ing, i) => (
                      <Badge key={i} variant="outline" className="text-[10px] border-[var(--sage)]/30 text-[var(--dark)]/40">{ing}</Badge>
                    ))}
                  </div>
                </AccordionSection>
              )}
              {product.how_to_use && (
                <AccordionSection title="Como Usar">
                  <p className="whitespace-pre-wrap">{product.how_to_use}</p>
                </AccordionSection>
              )}
            </div>
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="text-lg font-bold text-[var(--dark)] mb-6">Tambien te puede gustar</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
